import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import type { FunctionComponent } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import profilePlaceholder from '../../../assets/svg/onboarding/match-hat-profile-placeholder.svg';
import editIcon from '../../../assets/svg/onboarding/edit-profile-image.svg';
import userIcon from '../../../assets/svg/user-icon.svg';
import { PRESIGN_LINK } from '../../../graphql/PRESIGN_LINK';
import { SAVE_USER_AVATAR } from '../../../graphql/SAVE_USER_AVATAR';
import { GET_CURRENT_USER_me_avatar } from '../../../graphql/types/GET_CURRENT_USER';
import { getCompressedImagePath } from '../../../utils/getCompressedImagePath';
import { EUploadTypes, formatFilename, uploadToS3 } from '../../Upload';
import imageCompression from 'browser-image-compression';

interface IUserAvatarProps {
  avatar: GET_CURRENT_USER_me_avatar;
}

const UserAvatar: FunctionComponent<IUserAvatarProps> = (
  props: IUserAvatarProps
) => {
  const [file, setFile] = useState<File | null>(null);
  const [isAvatarHovered, setIsAvatarHovered] = useState(false);
  const saveAvatarCompleted = async () =>
    toast.success('User avatar successfully updated.');
  const [preSignLink] = useMutation(PRESIGN_LINK);
  const [saveUserAvatar] = useMutation(SAVE_USER_AVATAR, {
    onCompleted: saveAvatarCompleted
  });

  const backgroundImageUrl = file
    ? URL.createObjectURL(file)
    : props.avatar
    ? getCompressedImagePath(props.avatar.fileUrl, 'thumbnail')
    : userIcon;

  const toggleAvatarHover = () => setIsAvatarHovered(!isAvatarHovered);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    async function preSignUploadAndSaveDB(uploadFile: File) {
      try {
        const uploadType = EUploadTypes.avatar;
        const signedLink = await preSignLink({
          variables: {
            fileName: formatFilename(uploadFile.name),
            fileType: uploadFile.type,
            uploadType
          }
        });

        const { signedRequest, url } = signedLink.data.preSignLink;
        await uploadToS3(uploadFile, signedRequest);

        await saveUserAvatar({
          variables: { url }
        });
      } catch (e) {
        throw new Error(e.message);
      }
    }

    if (acceptedFiles.length > 0) {
      const compressedFile = await imageCompression(acceptedFiles[0], {
        maxSizeMB: 1 // Compress to be less than 1 MB
      });

      await setFile(compressedFile);
      await preSignUploadAndSaveDB(compressedFile);
    }
  }, []); // eslint-disable-line

  const onDropRejected = () =>
    toast.error('You can not upload files more than 5MB.', {
      autoClose: false
    });

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    maxSize: 5000000, // 5MB
    onDrop,
    onDropRejected
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <div
        className="page-user-profile-modal-card-avatar is-relative has-cursor-pointer"
        onMouseEnter={toggleAvatarHover}
        onMouseLeave={toggleAvatarHover}
      >
        <></>
        <figure
          className="is-96x96 avatar image m-b-1"
          style={{
            backgroundImage: `url(${backgroundImageUrl || profilePlaceholder})`
          }}
        >
            <button
              style={{
                backgroundColor: 'transparent',
                bottom: '0.5rem',
                right: '0.5rem'
              }}
              className=" has-height-auto is-borderless is-small is-absolute"
              aria-controls="edit-avatar"
            >
              <span className="icon">
                <img src={editIcon} alt="edit icon" />
              </span>
            </button>
        </figure>
      </div>
    </div>
  );
};

export default UserAvatar;
